import React, {useState } from 'react';
import styled from 'styled-components';
import {Link, useNavigate } from 'react-router-dom';

import {getAuth, signInWithEmailAndPassword } from 'firebase/auth';
// No longer need getFunctions or httpsCallable if OTP sending is paused here
// import {getFunctions, httpsCallable } from 'firebase/functions';

interface LoginPageProps {
}

// Styled components (no changes needed here from your previous code)
const LoginPageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - ${({theme }) => theme.spacing.xxl} * 2); /* Adjust height to account for header/footer if any */
  background-color: ${({theme }) => theme.colors.light};
  padding: ${({theme }) => theme.spacing.md};

  @media (max-width: ${({theme }) => theme.breakpoints.md}) {
    padding: ${({theme }) => theme.spacing.sm};
  }
`;

const LoginForm = styled.form`
  background-color: ${({theme }) => theme.colors.white};
  padding: ${({theme }) => theme.spacing.xl};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: ${({theme }) => theme.spacing.md};

  @media (max-width: ${({theme }) => theme.breakpoints.md}) {
    padding: ${({theme }) => theme.spacing.lg};
    gap: ${({theme }) => theme.spacing.sm};
  }
`;

const Title = styled.h2`
  text-align: center;
  color: ${({theme }) => theme.colors.primary};
  margin-bottom: ${({theme }) => theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({theme }) => theme.spacing.xs};
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: ${({theme }) => theme.colors.text};
  font-weight: bold;
`;

const Input = styled.input`
  padding: ${({theme }) => theme.spacing.sm};
  border: 1px solid ${({theme }) => theme.colors.textLight};
  border-radius: 4px;
  font-size: 1rem;
  outline: none;

  &:focus {
    border-color: ${({theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(${({theme }) => theme.colors.primary}, 0.2);
  }
`;

const Button = styled.button`
  padding: ${({theme }) => theme.spacing.md};
  background-color: ${({theme }) => theme.colors.primary};
  color: ${({theme }) => theme.colors.white};
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: ${({theme }) => theme.colors.secondary};
  }

  &:disabled {
    background-color: ${({theme }) => theme.colors.textLight};
    cursor: not-allowed;
  }
`;

const StyledLink = styled(Link)`
  text-align: center;
  color: ${({theme }) => theme.colors.primary};
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: ${({theme }) => theme.colors.secondary};
  }
`;

interface MessageProps {
  type?: 'success' | 'error';
}

const MessageContainer = styled.div<MessageProps>`
  padding: ${({theme }) => theme.spacing.sm};
  border-radius: 4px;
  text-align: center;
  font-size: 0.9rem;
  margin-bottom: ${({theme }) => theme.spacing.md};
  ${({type, theme }) => type === 'error' && `
    background-color: #ffe0e0;
    color: ${theme.colors.error || '#dc3545'};
    border: 1px solid ${theme.colors.error || '#dc3545'};
  `}
  ${({type, theme }) => type === 'success' && `
    background-color: ${theme.colors.success || '#e0ffe0'};
    color: ${theme.colors.success || '#28a745'};
    border: 1px solid ${theme.colors.success || '#28a745'};
  `}
`;

// Modify the Login component to no longer accept onLoginSuccess prop directly
const Login: React.FC<LoginPageProps> = () => {
  const auth = getAuth();
  const navigate = useNavigate();
  // Removed functions and callSendOtp imports/initialization

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'error' | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage(null);
    setMessageType(undefined);
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);

      setMessage('Login successful!');
      setMessageType('success');
      navigate('/');
      
    } catch (error: any) {
      console.error("Login error:", error);
      let errorMessage = 'Login failed. Please try again.';
      setMessageType('error');

      if (error.code) {
        switch (error.code) {
          case 'auth/invalid-email':
            errorMessage = 'Invalid email address.';
            break;
          case 'auth/user-disabled':
            errorMessage = 'Your account has been disabled.';
            break;
          case 'auth/user-not-found':
            errorMessage = 'No account found with this email.';
            break;
          case 'auth/wrong-password':
            errorMessage = 'Incorrect password. Please try again.';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Network error. Please check your internet connection.';
            break;
          default:
            errorMessage = `An unknown error occurred: ${error.message}`;
            break;
        }
      } else if (error.message) {
        errorMessage = `An unexpected error occurred: ${error.message}`;
      }
      setMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginPageContainer>
      <LoginForm onSubmit={handleSubmit}>
        <Title>Login to GREMA</Title>
        {message && <MessageContainer type={messageType}>{message}</MessageContainer>}
        <FormGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="password">Password</Label>
          <Input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
          />
        </FormGroup>
        <Button type="submit" disabled={loading}>
          {loading ? 'Logging In...' : 'Login'}
        </Button>
        <StyledLink to="/signup">Don't have an account? Sign up</StyledLink>      
      </LoginForm>
    </LoginPageContainer>
  );
};

export default Login;